import React from 'react';
import useThemeCanvas, { hexToRgb } from '../useThemeCanvas';

// Real-estate neighborhood skyline. Four parallax layers walk the viewer from
// distant downtown high-rises → mid-rise apartments → townhomes → single-family
// houses with pitched roofs, chimneys, porch lights, and foreground trees.
// The sky gets a moon + stars in dark mode, soft clouds in light mode.

const LAYER_SPEEDS = [0.04, 0.09, 0.18, 0.35];
const LAYER_ALPHAS = [0.22, 0.38, 0.58, 0.85];
const LAYER_GROUND = [0.70, 0.76, 0.82, 0.90]; // fraction of height where ground sits
const LAYER_TYPES = [
  // Far: only tall buildings (downtown skyline)
  ['highrise', 'highrise', 'midrise'],
  // Middle-far: mid-rise apartments
  ['midrise', 'apartment', 'apartment', 'highrise'],
  // Middle-near: apartments + townhomes
  ['apartment', 'townhome', 'townhome', 'apartment'],
  // Near: single-family houses + townhomes, + occasional tree
  ['house', 'house', 'townhome', 'house', 'tree'],
];

// Per-layer building density (pixels of average spacing)
const LAYER_SPACING = [140, 110, 90, 100];

// --- Helpers ----------------------------------------------------------------

function rand(min, max) { return min + Math.random() * (max - min); }
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

function makeBuilding(layer, xStart) {
  const type = pick(LAYER_TYPES[layer]);
  const scale = 1 - layer * 0.05; // far things slightly smaller

  if (type === 'tree') {
    return {
      type,
      x: xStart,
      width: rand(20, 34) * scale,
      heightRatio: rand(0.07, 0.11),
      leafPhase: rand(0, Math.PI * 2),
    };
  }

  if (type === 'house') {
    const width = rand(85, 130) * scale;
    const heightRatio = rand(0.12, 0.18);
    return {
      type,
      x: xStart,
      width,
      heightRatio,
      roofRatio: rand(0.45, 0.6),
      hasChimney: Math.random() > 0.35,
      chimneyLeft: Math.random() > 0.5,
      hasPorch: Math.random() > 0.4,
      windows: Math.random() > 0.3 ? 2 : 1,
      lit: Math.random() > 0.25 ? 1 : 0,
      twinklePhase: rand(0, Math.PI * 2),
    };
  }

  if (type === 'townhome') {
    const units = 2 + Math.floor(Math.random() * 3);
    const unitWidth = rand(38, 52) * scale;
    return {
      type,
      x: xStart,
      units,
      width: unitWidth * units,
      unitWidth,
      heightRatio: rand(0.16, 0.22),
      roofRatio: rand(0.25, 0.35),
      litPattern: Array.from({ length: units }, () => Math.random() > 0.3 ? 1 : 0),
      twinklePhase: rand(0, Math.PI * 2),
    };
  }

  if (type === 'apartment') {
    const width = rand(80, 130) * scale;
    const heightRatio = rand(0.30, 0.44);
    const floors = 4 + Math.floor(Math.random() * 4);
    const windowsPerFloor = Math.max(2, Math.floor(width / 22));
    return {
      type,
      x: xStart,
      width,
      heightRatio,
      floors,
      windowsPerFloor,
      hasBalconies: Math.random() > 0.35,
      hasRooftopAC: Math.random() > 0.4,
      hasAntenna: Math.random() > 0.6,
      litPattern: Array.from({ length: floors }, () =>
        Array.from({ length: windowsPerFloor }, () => Math.random() > 0.35 ? 1 : 0)
      ),
      twinklePhase: rand(0, Math.PI * 2),
    };
  }

  // highrise / midrise (tall, narrow, urban)
  const isMid = type === 'midrise';
  const width = (isMid ? rand(70, 100) : rand(50, 90)) * scale;
  const heightRatio = isMid ? rand(0.42, 0.55) : rand(0.55, 0.75);
  const floors = Math.floor((isMid ? 10 : 16) + Math.random() * (isMid ? 6 : 12));
  const windowsPerFloor = Math.max(3, Math.floor(width / 14));
  return {
    type,
    x: xStart,
    width,
    heightRatio,
    floors,
    windowsPerFloor,
    hasWaterTank: Math.random() > 0.4,
    hasSpire: !isMid && Math.random() > 0.55,
    litPattern: Array.from({ length: floors }, () =>
      Array.from({ length: windowsPerFloor }, () => Math.random() > 0.45 ? 1 : 0)
    ),
    twinklePhase: rand(0, Math.PI * 2),
  };
}

// --- Drawing primitives ------------------------------------------------------

function drawWindowGrid(ctx, b, topY, buildingHeight, palette, t, dark, colorMode) {
  const cols = b.windowsPerFloor;
  const rows = b.floors;
  const wCell = b.width / (cols + 1);
  const hCell = buildingHeight / (rows + 1);
  const winW = Math.max(2.5, wCell * 0.5);
  const winH = Math.max(3, hCell * 0.55);

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (!b.litPattern[row][col]) continue;
      const wx = b.x + wCell * (col + 0.75) - winW / 2;
      const wy = topY + hCell * (row + 0.75) - winH / 2;
      const idx = (row + col + Math.floor(t * (colorMode === 'cycling' ? 0.5 : 0.08))) % palette.length;
      const rgb = hexToRgb(palette[idx]);
      const twinkle = 0.55 + 0.45 * Math.sin(t * 1.2 + b.twinklePhase + row * 0.4 + col);
      const alpha = (dark ? 0.85 : 0.7) * twinkle;
      ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
      ctx.fillRect(wx, wy, winW, winH);
    }
  }
}

function drawHouse(ctx, b, groundY, silhouette, palette, t, dark, colorMode) {
  const bh = (b.heightRatio) * (groundY - 20);
  const topY = groundY - bh;
  const roofH = bh * b.roofRatio;
  const wallTopY = topY + roofH;

  // Pitched roof (triangle)
  ctx.fillStyle = silhouette;
  ctx.beginPath();
  ctx.moveTo(b.x, wallTopY);
  ctx.lineTo(b.x + b.width / 2, topY);
  ctx.lineTo(b.x + b.width, wallTopY);
  ctx.closePath();
  ctx.fill();

  // Walls
  ctx.fillRect(b.x, wallTopY, b.width, bh - roofH);

  // Chimney
  if (b.hasChimney) {
    const cx = b.chimneyLeft ? b.x + b.width * 0.28 : b.x + b.width * 0.65;
    const cw = b.width * 0.08;
    const ch = roofH * 0.7;
    ctx.fillRect(cx, topY + roofH * 0.15, cw, ch);
  }

  // Porch roof
  if (b.hasPorch) {
    const pw = b.width * 0.35;
    const px = b.x + (b.width - pw) / 2;
    const py = groundY - bh * 0.35;
    ctx.fillRect(px, py, pw, 3);
    // porch posts
    ctx.fillRect(px + 2, py, 2, groundY - py);
    ctx.fillRect(px + pw - 4, py, 2, groundY - py);
  }

  // Lit windows + porch light
  if (b.lit) {
    const accentIdx = Math.floor(t * (colorMode === 'cycling' ? 0.5 : 0.08)) % palette.length;
    const rgb = hexToRgb(palette[accentIdx]);
    const twinkle = 0.55 + 0.45 * Math.sin(t * 1.1 + b.twinklePhase);
    const alpha = (dark ? 0.9 : 0.75) * twinkle;

    // Window(s)
    const winW = b.width * 0.14;
    const winH = (bh - roofH) * 0.35;
    const wy = wallTopY + (bh - roofH - winH) * 0.35;
    if (b.windows === 2) {
      ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
      ctx.fillRect(b.x + b.width * 0.22, wy, winW, winH);
      ctx.fillRect(b.x + b.width * 0.64, wy, winW, winH);
    } else {
      ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
      ctx.fillRect(b.x + b.width * 0.4, wy, winW * 1.3, winH);
    }

    // Porch light (warm glow)
    if (b.hasPorch) {
      const warm = dark ? `rgba(253,224,71,${0.8 * twinkle})` : `rgba(234,179,8,${0.6 * twinkle})`;
      ctx.fillStyle = warm;
      const px = b.x + b.width * 0.5;
      const py = groundY - bh * 0.3;
      ctx.beginPath();
      ctx.arc(px, py, 2.2, 0, Math.PI * 2);
      ctx.fill();
      if (dark) {
        ctx.fillStyle = `rgba(253,224,71,${0.15 * twinkle})`;
        ctx.beginPath();
        ctx.arc(px, py, 7, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
}

function drawTownhome(ctx, b, groundY, silhouette, palette, t, dark, colorMode) {
  const bh = b.heightRatio * (groundY - 20);
  const topY = groundY - bh;
  const roofH = bh * b.roofRatio;
  const wallTopY = topY + roofH;

  // Sawtooth pitched roofs over each unit
  ctx.fillStyle = silhouette;
  for (let u = 0; u < b.units; u++) {
    const ux = b.x + u * b.unitWidth;
    ctx.beginPath();
    ctx.moveTo(ux, wallTopY);
    ctx.lineTo(ux + b.unitWidth / 2, topY);
    ctx.lineTo(ux + b.unitWidth, wallTopY);
    ctx.closePath();
    ctx.fill();
  }
  // Walls
  ctx.fillRect(b.x, wallTopY, b.width, bh - roofH);
  // Unit separators (thin vertical lines)
  const sep = dark ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.15)';
  ctx.fillStyle = sep;
  for (let u = 1; u < b.units; u++) {
    ctx.fillRect(b.x + u * b.unitWidth - 0.5, wallTopY, 1, bh - roofH);
  }

  // Windows per unit
  const winW = b.unitWidth * 0.35;
  const winH = (bh - roofH) * 0.3;
  const wy = wallTopY + (bh - roofH - winH) * 0.4;
  for (let u = 0; u < b.units; u++) {
    if (!b.litPattern[u]) continue;
    const idx = (u + Math.floor(t * (colorMode === 'cycling' ? 0.5 : 0.08))) % palette.length;
    const rgb = hexToRgb(palette[idx]);
    const twinkle = 0.55 + 0.45 * Math.sin(t * 1.1 + b.twinklePhase + u);
    const alpha = (dark ? 0.9 : 0.75) * twinkle;
    ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
    ctx.fillRect(b.x + u * b.unitWidth + (b.unitWidth - winW) / 2, wy, winW, winH);
  }
}

function drawApartment(ctx, b, groundY, silhouette, palette, t, dark, colorMode) {
  const bh = b.heightRatio * (groundY - 20);
  const topY = groundY - bh;

  // Flat-roof main mass
  ctx.fillStyle = silhouette;
  ctx.fillRect(b.x, topY, b.width, bh);

  // Parapet
  ctx.fillRect(b.x - 2, topY - 4, b.width + 4, 4);

  // Rooftop AC units
  if (b.hasRooftopAC) {
    const acW = b.width * 0.15;
    ctx.fillRect(b.x + b.width * 0.3, topY - 9, acW, 5);
    ctx.fillRect(b.x + b.width * 0.6, topY - 7, acW * 0.7, 4);
  }

  // Antenna
  if (b.hasAntenna) {
    ctx.fillRect(b.x + b.width * 0.5, topY - 18, 1.5, 14);
  }

  // Balconies (horizontal bands)
  if (b.hasBalconies) {
    const floorH = bh / (b.floors + 1);
    const balconyColor = dark ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.15)';
    ctx.fillStyle = balconyColor;
    for (let f = 1; f < b.floors; f++) {
      ctx.fillRect(b.x + 2, topY + floorH * f + floorH * 0.65, b.width - 4, 1.5);
    }
  }

  drawWindowGrid(ctx, b, topY, bh, palette, t, dark, colorMode);
}

function drawHighrise(ctx, b, groundY, silhouette, palette, t, dark, colorMode) {
  const bh = b.heightRatio * (groundY - 20);
  const topY = groundY - bh;

  // Taper (trapezoid — slightly narrower at top)
  ctx.fillStyle = silhouette;
  ctx.beginPath();
  ctx.moveTo(b.x, groundY);
  ctx.lineTo(b.x + b.width, groundY);
  ctx.lineTo(b.x + b.width * 0.92, topY);
  ctx.lineTo(b.x + b.width * 0.08, topY);
  ctx.closePath();
  ctx.fill();

  // Water tower
  if (b.hasWaterTank) {
    const tx = b.x + b.width * 0.25;
    const tw = b.width * 0.22;
    ctx.fillRect(tx, topY - 10, tw, 10);
    ctx.fillRect(tx - 1, topY - 13, tw + 2, 3);
    // Legs
    ctx.fillRect(tx + tw * 0.15, topY - 14, 1.5, 4);
    ctx.fillRect(tx + tw * 0.75, topY - 14, 1.5, 4);
  }

  // Spire
  if (b.hasSpire) {
    const sx = b.x + b.width * 0.5;
    ctx.beginPath();
    ctx.moveTo(sx - 2, topY);
    ctx.lineTo(sx, topY - 22);
    ctx.lineTo(sx + 2, topY);
    ctx.closePath();
    ctx.fill();
    // Aviation light
    const blink = Math.floor(t) % 2 === 0;
    if (blink) {
      ctx.fillStyle = 'rgba(239,68,68,0.9)';
      ctx.beginPath();
      ctx.arc(sx, topY - 22, 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = silhouette;
    }
  }

  drawWindowGrid(ctx, b, topY, bh, palette, t, dark, colorMode);
}

function drawTree(ctx, b, groundY, silhouette, t) {
  const h = b.heightRatio * (groundY - 20);
  const topY = groundY - h;
  const sway = Math.sin(t * 0.8 + b.leafPhase) * 1.2;

  // Trunk
  ctx.fillStyle = silhouette;
  ctx.fillRect(b.x + b.width / 2 - 1.5, groundY - h * 0.4, 3, h * 0.4);

  // Canopy (3 overlapping ellipses for a leafy shape)
  ctx.beginPath();
  ctx.ellipse(b.x + b.width / 2 + sway, topY + h * 0.25, b.width * 0.55, h * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(b.x + b.width * 0.3 + sway, topY + h * 0.4, b.width * 0.42, h * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(b.x + b.width * 0.7 + sway, topY + h * 0.4, b.width * 0.42, h * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();
}

// --- Main component ---------------------------------------------------------

export default function ComplianceTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.layers = LAYER_SPACING.map((spacing, layer) => {
        const buildings = [];
        let x = -spacing;
        while (x < w + spacing) {
          const b = makeBuilding(layer, x);
          buildings.push(b);
          x += b.width + rand(spacing * 0.25, spacing * 0.5);
        }
        return buildings;
      });
      // Stars (fixed positions)
      state.stars = Array.from({ length: 60 }, () => ({
        x: Math.random() * w,
        y: Math.random() * h * 0.5,
        r: Math.random() * 0.8 + 0.3,
        phase: Math.random() * Math.PI * 2,
      }));
      // Moon position
      state.moon = {
        x: w * (0.75 + Math.random() * 0.15),
        y: h * (0.15 + Math.random() * 0.1),
        r: 30 + Math.random() * 10,
      };
      // Drifting clouds (light mode)
      state.clouds = Array.from({ length: 5 }, () => ({
        x: Math.random() * w,
        y: h * (0.08 + Math.random() * 0.2),
        w: 80 + Math.random() * 80,
        h: 18 + Math.random() * 10,
        speed: 0.05 + Math.random() * 0.1,
      }));
    },
    draw: (ctx, w, h, state, t) => {
      // Sky
      const sky = ctx.createLinearGradient(0, 0, 0, h);
      if (dark) {
        sky.addColorStop(0, '#020617');
        sky.addColorStop(0.45, '#0f172a');
        sky.addColorStop(0.85, '#312e81');
        sky.addColorStop(1, '#6b21a8');
      } else {
        sky.addColorStop(0, '#dbeafe');
        sky.addColorStop(0.6, '#bfdbfe');
        sky.addColorStop(1, '#fde68a');
      }
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, w, h);

      // Moon (dark) or sun-glow (light)
      if (dark) {
        // Moon glow
        const grad = ctx.createRadialGradient(state.moon.x, state.moon.y, 0, state.moon.x, state.moon.y, state.moon.r * 3);
        grad.addColorStop(0, 'rgba(255,241,220,0.5)');
        grad.addColorStop(1, 'rgba(255,241,220,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(state.moon.x, state.moon.y, state.moon.r * 3, 0, Math.PI * 2);
        ctx.fill();
        // Moon disc
        ctx.fillStyle = 'rgba(252,245,227,0.95)';
        ctx.beginPath();
        ctx.arc(state.moon.x, state.moon.y, state.moon.r, 0, Math.PI * 2);
        ctx.fill();
        // Subtle craters
        ctx.fillStyle = 'rgba(200,190,170,0.15)';
        ctx.beginPath();
        ctx.arc(state.moon.x - state.moon.r * 0.3, state.moon.y - state.moon.r * 0.2, state.moon.r * 0.15, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(state.moon.x + state.moon.r * 0.25, state.moon.y + state.moon.r * 0.3, state.moon.r * 0.1, 0, Math.PI * 2);
        ctx.fill();

        // Stars
        for (const s of state.stars) {
          const twinkle = 0.4 + 0.6 * Math.abs(Math.sin(t * 1.5 + s.phase));
          ctx.fillStyle = `rgba(255,255,255,${twinkle * 0.9})`;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fill();
        }
      } else {
        // Sun glow
        const sunX = w * 0.82;
        const sunY = h * 0.2;
        const grad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, 200);
        grad.addColorStop(0, 'rgba(253,230,138,0.7)');
        grad.addColorStop(1, 'rgba(253,230,138,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h * 0.5);

        // Clouds
        for (const c of state.clouds) {
          c.x -= c.speed;
          if (c.x + c.w < -20) c.x = w + 20;
          ctx.fillStyle = 'rgba(255,255,255,0.7)';
          ctx.beginPath();
          ctx.ellipse(c.x, c.y, c.w * 0.4, c.h, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.ellipse(c.x + c.w * 0.25, c.y - 4, c.w * 0.28, c.h * 0.85, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.ellipse(c.x + c.w * 0.5, c.y, c.w * 0.35, c.h, 0, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Parallax layers (far → near)
      for (let layer = 0; layer < state.layers.length; layer++) {
        const buildings = state.layers[layer];
        const speed = LAYER_SPEEDS[layer];
        const layerAlpha = LAYER_ALPHAS[layer];
        const groundY = h * LAYER_GROUND[layer];

        // Silhouette color for this layer
        const silhouette = dark
          ? `rgba(8,12,30,${Math.min(0.95, layerAlpha + 0.3)})`
          : `rgba(30,41,59,${layerAlpha})`;

        for (const b of buildings) {
          b.x -= speed;
          if (b.x + b.width < -40) {
            // Respawn on the right
            const maxX = Math.max(...buildings.map(bb => bb.x + bb.width));
            const next = makeBuilding(layer, maxX + rand(LAYER_SPACING[layer] * 0.25, LAYER_SPACING[layer] * 0.6));
            Object.assign(b, next);
          }

          if (b.type === 'tree') {
            drawTree(ctx, b, groundY, silhouette, t);
          } else if (b.type === 'house') {
            drawHouse(ctx, b, groundY, silhouette, palette, t, dark, colorMode);
          } else if (b.type === 'townhome') {
            drawTownhome(ctx, b, groundY, silhouette, palette, t, dark, colorMode);
          } else if (b.type === 'apartment') {
            drawApartment(ctx, b, groundY, silhouette, palette, t, dark, colorMode);
          } else {
            drawHighrise(ctx, b, groundY, silhouette, palette, t, dark, colorMode);
          }
        }

        // Ground fill below the layer's baseline (keeps silhouettes grounded)
        ctx.fillStyle = silhouette;
        ctx.fillRect(0, groundY, w, h - groundY);
      }

      // Ground haze
      const haze = ctx.createLinearGradient(0, h * 0.75, 0, h);
      haze.addColorStop(0, 'rgba(0,0,0,0)');
      haze.addColorStop(1, dark ? 'rgba(3,7,18,0.75)' : 'rgba(30,41,59,0.35)');
      ctx.fillStyle = haze;
      ctx.fillRect(0, h * 0.75, w, h * 0.25);
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
